/**
 * API client for ForbrugerAgenten Mobile App
 * Adapted from frontend/src/lib/api.ts — same endpoints, SecureStore instead of localStorage
 */
import axios from 'axios'
import { storage, TOKEN_KEY, USER_ID_KEY } from '../utils/storage'
import { captureException } from '../lib/posthog'
import { sentryCaptureException } from '../lib/sentry'
import { appEvents } from '../lib/events'

let lastErrorFeedbackAt = 0
const ERROR_FEEDBACK_COOLDOWN_MS = 600_000 // 10 min between error feedback triggers

const DEV_API_URL = 'http://localhost:8002'
const PROD_API_URL = 'https://forbrugeragent-backend-production.up.railway.app'

// EXPO_PUBLIC_API_URL overrides default (for Railway web deployment)
const ENV_API_URL = process.env.EXPO_PUBLIC_API_URL
export const API_URL = ENV_API_URL || (__DEV__ ? DEV_API_URL : PROD_API_URL)

export const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Decode JWT payload without crypto (just parse base64 — no signature verification)
export function getJwtExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
    return decoded.exp ?? null
  } catch {
    return null
  }
}

let _refreshPromise: Promise<void> | null = null

async function proactiveRefresh(token: string): Promise<string> {
  const exp = getJwtExpiry(token)
  if (!exp) return token
  const secsUntilExpiry = exp - Math.floor(Date.now() / 1000)
  // Refresh if < 2 hours left
  if (secsUntilExpiry > 7200) return token
  // Deduplicate concurrent refresh calls
  if (!_refreshPromise) {
    _refreshPromise = (async () => {
      try {
        const res = await axios.post(
          `${API_URL}/api/v1/auth/refresh`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 }
        )
        const newToken: string = res.data?.access_token
        if (newToken) await storage.set(TOKEN_KEY, newToken)
      } catch {
        // Ignore — let the request proceed with current token
      } finally {
        _refreshPromise = null
      }
    })()
  }
  await _refreshPromise
  return (await storage.get(TOKEN_KEY)) ?? token
}

// Auth token interceptor — attaches token and proactively refreshes if < 2h left
api.interceptors.request.use(async (config) => {
  let token = await storage.get(TOKEN_KEY)
  if (token && !(config as any)._skipAuthRedirect) {
    token = await proactiveRefresh(token)
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// 401 handler — sets flag for AuthProvider to handle navigation
let onUnauthorized: (() => void) | null = null

export function setOnUnauthorized(callback: () => void) {
  onUnauthorized = callback
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || ''
      if (url.includes('/admin/') || (error.config as any)?._skipAuthRedirect) {
        return Promise.reject(error)
      }
      await storage.remove(TOKEN_KEY)
      await storage.remove(USER_ID_KEY)
      onUnauthorized?.()
    }
    // Capture API errors to PostHog + Sentry (skip 401 which is handled above)
    if (error.response?.status !== 401) {
      const errorContext = {
        source: 'api_interceptor',
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      }
      captureException(error, errorContext)
      sentryCaptureException(error, errorContext)
      // Trigger feedback on 5xx server errors (max once per 10 min)
      if (error.response?.status >= 500 && Date.now() - lastErrorFeedbackAt > ERROR_FEEDBACK_COOLDOWN_MS) {
        lastErrorFeedbackAt = Date.now()
        appEvents.emit('open-feedback', {
          trigger: 'error_encountered',
          question: 'Noget gik galt — fortæl os hvad der skete',
        })
      }
    }
    return Promise.reject(error)
  }
)

// ─── API Endpoints (identical to web) ────────────────────

export const apiClient = {
  // Users
  users: {
    login: (data: any) => api.post('/users/login', data),
    create: (data: any) => api.post('/users/', data),
    get: (userId: string) => api.get(`/users/${userId}`),
    getMe: (userId: string) => api.get(`/users/me?user_id=${userId}`),
    update: (userId: string, data: any) => api.patch(`/users/${userId}`, data),
    updateConsent: (userId: string, data: any) => api.post(`/users/${userId}/consent`, data),
    exportData: (userId: string) => api.get(`/users/${userId}/export`, { responseType: 'blob' }),
    delete: (userId: string) => api.delete(`/users/${userId}`),
    registerPushToken: (userId: string, token: string) =>
      api.post(`/users/${userId}/push-token`, { token }),
    deregisterPushToken: (userId: string) =>
      api.delete(`/users/${userId}/push-token`),
  },

  // Onboarding
  onboarding: {
    status: () => api.get('/onboarding/status'),
    saveCpr: (cpr_number: string) => api.post('/onboarding/cpr', { cpr_number }),
  },

  // Contracts
  contracts: {
    list: (userId: string) => api.get(`/contracts/?user_id=${userId}`),
    create: (data: any) => api.post('/contracts/', data),
    get: (id: string) => api.get(`/contracts/${id}`),
    update: (id: string, data: any) => api.patch(`/contracts/${id}`, data),
    delete: (id: string) => api.delete(`/contracts/${id}`),
    simulate: (categories: string[]) =>
      api.post(`/contracts/simulate?${categories.map(c => `categories=${c}`).join('&')}`),
    clearSimulation: () => api.delete('/contracts/simulate'),
    simulateSwitch: (category: string, previousProvider: string, newProvider: string, newPrice: number) =>
      api.post(`/contracts/simulate-switch?category=${category}&previous_provider=${encodeURIComponent(previousProvider)}&new_provider=${encodeURIComponent(newProvider)}&new_price=${newPrice}`),
    clearSwitches: () => api.delete('/contracts/simulate-switch'),
    billHistory: () => api.get('/contracts/bill-history'),
  },

  // Quotes
  quotes: {
    list: (userId: string) => api.get(`/quotes/?user_id=${userId}`),
    getRequests: (userId: string) => api.get(`/quotes/requests?user_id=${userId}`),
    createRequest: (data: any) => api.post('/quotes/requests', data),
    accept: (quoteId: string, data?: Record<string, any>, config?: any) => api.post(`/quotes/${quoteId}/accept`, data || {}, config),
    completeRequest: (requestId: string) => api.post(`/quotes/requests/${requestId}/complete`),
    cancelRequest: (requestId: string) => api.post(`/quotes/requests/${requestId}/cancel`),
    cancelSwitch: (category: string) => api.post(`/quotes/cancel-switch/${category}`),
    retrySwitch: (category: string) => api.post(`/quotes/retry-switch/${category}`),
    requestBulk: (data: any, userId?: string) => api.post('/quotes/requests/bulk', data),
  },

  // Providers
  providers: {
    list: (category?: string) => api.get(category ? `/providers/?category=${category}` : '/providers/'),
  },

  // Policies (legacy - maps to contracts)
  policies: {
    list: (userId: string) => api.get(`/contracts/?user_id=${userId}`),
  },

  // Inbox
  inbox: {
    list: (params?: Record<string, string>) => api.get('/inbox', { params }),
    getPreferences: () => api.get('/inbox/preferences'),
    updatePreferences: (data: any) => api.put('/inbox/preferences', data),
    markRead: (messageId: string) => api.post(`/inbox/${messageId}/read`),
    markAllRead: () => api.post('/inbox/read-all'),
    archive: (messageId: string) => api.post(`/inbox/${messageId}/archive`),
    delete: (messageId: string) => api.delete(`/inbox/${messageId}`),
    providerTimeline: (providerId: string) => api.get(`/inbox/provider/${providerId}/timeline`),
    composeDraft: (providerId: string) => api.post(`/inbox/provider/${providerId}/compose-draft`),
    sendComposed: (data: any) => api.post('/inbox/send-composed', data),
    respond: (messageId: string, data: { value: string; identifier_field: string }) =>
      api.post(`/inbox/${messageId}/respond`, data),
    getFullBody: (messageId: string) => api.get(`/inbox/${messageId}/body`),
    downloadAttachment: (messageId: string, attachmentId: string) =>
      api.get(`/inbox/${messageId}/attachments/${attachmentId}`, { responseType: 'blob' }),
  },

  // Support & Activity
  support: {
    createTicket: (data: any) => api.post('/support/tickets', data),
    getTickets: (userId: string) => api.get(`/support/tickets?user_id=${userId}`),
    addMessage: (ticketId: string, message: string) => api.post(`/support/tickets/${ticketId}/message`, { message }),
  },
  activity: {
    getFeed: (userId: string) => api.get(`/activity/?user_id=${userId}`),
  },
  notifications: {
    markAllRead: () => api.post('/notifications/mark-all-read'),
  },
  feedback: {
    submit: (data: { rating: number; comment: string | null; page_url: string; trigger: string }) =>
      api.post('/feedback/', data),
  },

  nps: {
    submit: (data: { score: number; followup_answer: string | null }) =>
      api.post('/nps/', data),
  },

  // Auth
  auth: {
    login: (redirectUri?: string) => api.post('/auth/login', redirectUri ? { redirect_uri: redirectUri } : {}),
    callback: (code: string, state: string) => api.post('/auth/callback', { code, state }),
    me: (token: string) =>
      api.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } }),
  },

  // AI Chat
  chat: {
    send: (
      message: string,
      conversationHistory?: Array<{ role: string; content: string }>,
      inboxMessageId?: string,
      providerId?: string,
      dashboardState?: {
        total_savings: number
        savings_by_category: Record<string, number>
        best_per_category: Record<string, { provider: string; monthly_price: number; yearly_savings: number }>
      },
    ) =>
      api.post('/ai-agent/chat', {
        message,
        conversation_history: conversationHistory || undefined,
        inbox_message_id: inboxMessageId || undefined,
        provider_id: providerId || undefined,
        dashboard_state: dashboardState || undefined,
      }, { timeout: 120000 }),
    getContext: () => api.get('/ai-agent/context'),
    sendDraft: (draft: {
      draft_id: string
      to_email: string
      subject: string
      body: string
      provider_name?: string
      provider_id?: string
      in_reply_to_message_id?: string
    }) => api.post('/ai-agent/send-draft', draft),
  },

  // Electricity tariffs
  tariffs: {
    current: (area?: string, dso?: string) => {
      const params: Record<string, string> = {}
      if (area) params.area = area
      if (dso) params.dso = dso
      return api.get('/tariffs/current', { params })
    },
    dsoList: () => api.get('/tariffs/dso-list'),
  },

  // Email connections (Unipile)
  email: {
    authorize: (provider: string, redirectTo?: string) =>
      api.post('/email/connect/authorize', { provider, redirect_to: redirectTo }),
    connectImap: (email: string, password: string, providerKey: string = 'onecom') =>
      api.post('/email/connect/imap', { email, password, provider_key: providerKey }),
    status: () => api.get('/email/status'),
    sync: (accountId?: string) => api.post('/email/sync', accountId ? { account_id: accountId } : {}),
    disconnect: (connectionId: string) => api.delete(`/email/${connectionId}/disconnect`),
    scanConsent: (connectionId: string) => api.post(`/email/${connectionId}/scan/consent`),
    detectProviders: (connectionId: string) => api.post(`/email/${connectionId}/scan/detect`),
    fullScan: (connectionId: string) => api.post(`/email/${connectionId}/scan/full`),
  },

  // Public market prices (no auth)
  publicPrices: {
    cheapest: (category: string, limit?: number, filters?: Record<string, string>) => {
      const params: Record<string, string> = { category, limit: String(limit || 5) }
      if (filters) Object.assign(params, filters)
      return api.get('/public-prices/cheapest', { params })
    },
  },

  upload: {
    bill: (userId: string, formData: FormData) =>
      api.post(`/upload/bill?user_id=${userId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      }),
  },

  // Feature Flags
  flags: {
    get: () => api.get('/flags/'),
    adminGet: () => api.get('/admin/flags/'),
    adminUpdate: (data: Record<string, boolean>) => api.patch('/admin/flags/', { flags: data }),
  },
}
